<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Add" }} Postpartum Assessment</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Assessed At *</label>
              <input type="datetime-local" v-model="form.assessed_at" required class="form-control" />
            </div>
            <div class="form-group">
              <label>Hours Post Delivery</label>
              <input type="number" v-model="form.hours_post_delivery" min="0" step="0.5" class="form-control" />
            </div>

            <div class="form-group">
              <label>Uterine Tone</label>
              <select v-model="form.uterine_tone" class="form-control">
                <option value="">Select</option>
                <option value="Firm">Firm</option>
                <option value="Boggy">Boggy</option>
              </select>
            </div>
            <div class="form-group">
              <label>Fundal Height</label>
              <input type="text" v-model="form.fundal_height" class="form-control" />
            </div>
            <div class="form-group">
              <label>Lochia</label>
              <select v-model="form.lochia" class="form-control">
                <option value="">Select</option>
                <option value="Rubra">Rubra</option>
                <option value="Serosa">Serosa</option>
                <option value="Alba">Alba</option>
                <option value="Heavy/Excessive">Heavy/Excessive</option>
              </select>
            </div>

            <div class="form-group">
              <label>Perineum Condition</label>
              <input type="text" v-model="form.perineum_condition" class="form-control" />
            </div>
            <div class="form-group">
              <label>Breast Condition</label>
              <input type="text" v-model="form.breast_condition" class="form-control" />
            </div>

            <div class="form-group">
              <label>BP</label>
              <div class="bp-inputs">
                <input type="number" v-model="form.bp_systolic" placeholder="Systolic" class="form-control" />
                <span class="bp-separator">/</span>
                <input type="number" v-model="form.bp_diastolic" placeholder="Diastolic" class="form-control" />
              </div>
            </div>
            <div class="form-group">
              <label>Pulse (bpm)</label>
              <input type="number" v-model="form.pulse" class="form-control" />
            </div>
            <div class="form-group">
              <label>Temperature (°C)</label>
              <input type="number" v-model="form.temperature" step="0.1" class="form-control" />
            </div>

            <div class="form-group">
              <label>Urine Output</label>
              <input type="text" v-model="form.urine_output" class="form-control" />
            </div>
            <div class="form-group">
              <label>Pain Score (0-10)</label>
              <input type="number" v-model="form.pain_score" min="0" max="10" class="form-control" />
            </div>
            <div class="form-group">
              <label>Psychological State</label>
              <input type="text" v-model="form.psychological_state" class="form-control" />
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.family_planning_counseling_given" />
                Family planning counseling given
              </label>
            </div>
            <div class="form-group" v-if="form.family_planning_counseling_given">
              <label>Method Chosen</label>
              <input type="text" v-model="form.family_planning_method_chosen" class="form-control" />
            </div>

            <div class="form-group full-width">
              <label>Complications</label>
              <textarea v-model="form.complications" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="form.notes" class="form-control" rows="3"></textarea>
            </div>

            <div class="form-group">
              <label>Recorded By</label>
              <input type="text" v-model="form.recorded_by" class="form-control" />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : (editingRecord ? "Update" : "Save") + " Assessment" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "PostpartumModal",
  props: {
    visitId: { type: [Number, String], default: null },
    editRecord: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        ...this.emptyForm(),
        ...this.editRecord,
        assessed_at: this.toLocalDateTime(this.editRecord.assessed_at),
      };
    }
  },
  methods: {
    emptyForm() {
      return {
        assessed_at: this.toLocalDateTime(new Date()),
        hours_post_delivery: "",
        uterine_tone: "",
        fundal_height: "",
        lochia: "",
        perineum_condition: "",
        breast_condition: "",
        bp_systolic: "",
        bp_diastolic: "",
        pulse: "",
        temperature: "",
        urine_output: "",
        pain_score: "",
        psychological_state: "",
        family_planning_counseling_given: false,
        family_planning_method_chosen: "",
        complications: "",
        notes: "",
        recorded_by: "",
      };
    },
    toLocalDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    closeModal() {
      this.$emit("close");
    },
    async saveRecord() {
      this.saving = true;
      try {
        const payload = { ...this.form, visit_id: this.visitId };
        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(`postpartum_assessments/${this.editingRecord.id}`, "PUT", payload, 1);
        } else {
          response = await generalApiRequest("postpartum_assessments", "POST", payload, 1);
        }
        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save assessment");
        }
      } catch (error) {
        console.error("Error saving postpartum assessment:", error);
        alert("An error occurred while saving the assessment");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.mmodal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
.modal-header { padding: 20px; background: linear-gradient(135deg, #9f7aea 0%, #6b46c1 100%); color: white; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 20px; }
.close { background: rgb(233, 22, 22); border: 1px solid rgb(182, 178, 178); font-size: 1.5rem; color: white; line-height: 1; cursor: pointer; }
.modal-body { padding: 30px; overflow-y: auto; flex-grow: 1; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 15px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; }
.checkbox-label { display: flex !important; align-items: center; gap: 8px; margin-top: 28px; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; }
.bp-inputs { display: flex; align-items: center; gap: 10px; }
.bp-inputs .form-control { flex: 1; }
.bp-separator { font-weight: bold; color: #495057; }
.modal-actions { display: flex; justify-content: flex-end; gap: 15px; }
.btn { padding: 10px 25px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
.btn-secondary { background: #6c757d; color: white; }
.btn-secondary:hover { background: #5a6268; }
.btn-primary { background: #6b46c1; color: white; }
.btn-primary:hover { background: #553c9a; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 1200px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
